import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportAbsenceModal from "../../components/common/ReportAbsenceModal";
import apiCoreAdapter from "../../hooks/apiCoreAdapter";
import { useAppShell } from "../../contexts/AppShellContext";
import { isImpersonated } from "../../hooks/api/auth/tokenManager";
import type { MeetSession } from "../../hooks/api/meet";
import { usePatientDetail } from "../../hooks/patients/usePatientDetail";
import { ClinicalTab } from "./ClinicalTab";
import { ExitGate } from "./ExitGate";
import { EmergencyContact, InfoTab } from "./InfoTab";
import { meetBtn } from "./MeetModal";
import { decideExit } from "./meetRules";
import { formatClock } from "./meetTime";
import { PoliciesTab } from "./PoliciesTab";
import { UrgencyDialog } from "./UrgencyDialog";
import { useMeetPresence } from "./useMeetPresence";

/** Origen de la videollamada: solo se embebe una sala de psi-video. */
const MEET_ORIGIN = "https://meet.psimammoliti.com";
/** Si la llamada no avisa nada en este tiempo (no cargó), se libera la navegación para no dejar a nadie encerrada. */
const SILENT_UNLOCK_MS = 30_000;
const GAP_PX = 8;

const STATE_TEXT: Record<string, string> = {
  ready: "Cargando…",
  waiting: "Esperando que abra la sala",
  lobby: "Previa: elegí cámara y micrófono",
  joined: "En la llamada",
  left: "Saliste de la llamada",
  ended: "La reunión terminó",
  error: "Hubo un error en la llamada",
};
const FINISHED = ["left", "ended", "error", "invalid"];

type Tab = "clinical" | "info" | "policies";
const TABS: [Tab, string][] = [
  ["clinical", "Historia clínica"],
  ["info", "Información"],
  ["policies", "Políticas"],
];

/**
 * La llamada embebida (iframe de psi-video) con el panel privado de la psicóloga al costado. El panel vive FUERA del iframe: nunca viaja en el
 * video. Arranca cerrado, se esconde con Alt+P y se oculta solo mientras comparte pantalla. La salida la administra esta pantalla (política de
 * tolerancia): el único botón para salir es el de la propia llamada. Mientras la llamada está abierta, la navegación del encabezado queda bloqueada.
 */
export function CallWorkspace({
  url,
  linkToken,
  session,
  onClose,
}: {
  url: string;
  linkToken: string;
  session: MeetSession;
  onClose: () => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const onLeaveRequested = useRef<() => void>(() => undefined);
  const frameBox = useRef<HTMLDivElement>(null);
  const [state, setState] = useState("ready");
  const [panelOpen, setPanelOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const reopenAfterShare = useRef(false);
  const [tab, setTab] = useState<Tab>("clinical");
  const [exitOpen, setExitOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [urgencyOpen, setUrgencyOpen] = useState(false);
  const [absenceReported, setAbsenceReported] = useState(false);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [silent, setSilent] = useState(false);
  const [height, setHeight] = useState(640);

  const impersonated = isImpersonated();
  const { setCallActive } = useAppShell();
  const finished = FINISHED.includes(state);
  // Mientras la llamada está abierta, la navegación del encabezado queda bloqueada (se libera al terminar o si la llamada no carga)
  useEffect(() => {
    setCallActive?.(!finished && !silent);
    return () => setCallActive?.(false);
  }, [setCallActive, finished, silent]);
  useEffect(() => {
    if (state !== "ready") return undefined;
    const t = setTimeout(() => setSilent(true), SILENT_UNLOCK_MS);
    return () => clearTimeout(t);
  }, [state]);

  const { data: patient } = usePatientDetail(session.processId);
  const presence = useMeetPresence(linkToken, session.sessionId, !finished);
  const startMs = Date.parse(session.start);

  useEffect(() => {
    apiCoreAdapter
      .listEmergencyContacts(session.processId)
      .then(setContacts)
      .catch(() => undefined);
  }, [session.processId]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // La llamada ocupa todo el alto que queda en pantalla: sin scroll propio ni de la página
  const showBanner = !!presence?.absence.show && state === "joined";
  useLayoutEffect(() => {
    const fit = () => {
      const el = frameBox.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(420, Math.floor(window.innerHeight - top - GAP_PX)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [showBanner, finished]);

  const postToCall = useCallback((message: Record<string, unknown>) => {
    try {
      frame.current?.contentWindow?.postMessage(
        { target: "psi-meet", ...message },
        MEET_ORIGIN,
      );
    } catch {
      /* la llamada ya no está */
    }
  }, []);
  useEffect(() => {
    const h = (e: MessageEvent) => {
      if (e.origin !== MEET_ORIGIN || e.source !== frame.current?.contentWindow)
        return;
      const d = e.data as {
        source?: string;
        type?: string;
        on?: unknown;
      } | null;
      if (d?.source !== "psi-meet" || typeof d.type !== "string") return;
      if (d.type === "leave-requested") onLeaveRequested.current();
      else if (d.type === "sharing") setSharing(d.on === true);
      else {
        setState(d.type);
        // Se repite el pedido en cada aviso de la llamada: así llega aunque haya cargado después del primero
        if (d.type === "ready" || d.type === "lobby" || d.type === "joined")
          postToCall({ type: "manage-leave", on: true });
      }
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, [postToCall]);
  // La salida la administra esta pantalla: se lo pedimos a la llamada apenas carga
  const onFrameLoad = () => postToCall({ type: "manage-leave", on: true });

  // Al compartir pantalla, lo privado se oculta solo (panel y aviso de urgencia); al dejar de compartir, el panel vuelve como estaba
  useEffect(() => {
    if (sharing) {
      reopenAfterShare.current = panelOpen;
      setPanelOpen(false);
      setUrgencyOpen(false);
    } else if (reopenAfterShare.current) {
      reopenAfterShare.current = false;
      setPanelOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing]);

  // Atajo para esconder el panel (por si se comparte pantalla de otra forma)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === "p" && !sharing)
        setPanelOpen((o) => !o);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [sharing]);

  // Cerrar la pestaña con la sesión en curso pide confirmación
  useEffect(() => {
    if (state !== "joined") return undefined;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [state]);

  const leave = () => {
    setExitOpen(false);
    postToCall({ type: "leave" });
  };
  const decision = useMemo(
    () => decideExit(presence, absenceReported),
    [presence, absenceReported],
  );
  // Colgar desde la llamada: si la política no pide nada, corta; si no, abre el aviso (espera de la paciente / sesión corta)
  onLeaveRequested.current = () => {
    if (decision.kind === "free") leave();
    else setExitOpen(true);
  };

  const elapsedMs = now - startMs;
  const patientName = patient?.fullname || "Videollamada";

  return (
    <div className="w-full px-3 pb-2 pt-2 text-gray-900">
      <div className="mb-2 flex min-h-[40px] flex-wrap items-center gap-2">
        <span className="text-[15px] font-bold">{patientName}</span>
        {finished ? (
          <>
            <span className="text-xs font-medium text-gray-600" role="status">
              {STATE_TEXT[state] ?? "La llamada terminó."}
            </span>
            <button className={meetBtn.primarySm} onClick={onClose}>
              Volver a mis sesiones
            </button>
          </>
        ) : (
          <span
            className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-[#6D57BC]"
            role="status"
          >
            {STATE_TEXT[state] ?? state}
          </span>
        )}
        <span
          className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium tabular-nums text-gray-700"
          title="Tiempo desde el inicio de la sesión"
        >
          {elapsedMs >= 0
            ? formatClock(elapsedMs)
            : `Empieza en ${formatClock(-elapsedMs)}`}
        </span>
        {elapsedMs >= 50 * 60_000 && elapsedMs < 60 * 60_000 && (
          <span className="text-xs font-medium text-amber-700">
            50 min: cerrando
          </span>
        )}
        {elapsedMs >= 60 * 60_000 && (
          <span className="text-xs font-medium text-red-600">
            Pasaron los 60 min
          </span>
        )}
        {showBanner && presence && (
          <button
            onClick={() => setAbsenceOpen(true)}
            role="status"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 transition hover:bg-amber-100"
            title={`Tu paciente no se conectó: pasaron ${presence.absence.minutesElapsed} min${
              presence.absence.minutesToTolerance > 0
                ? ` y faltan ${presence.absence.minutesToTolerance} para la tolerancia`
                : " y ya pasó la tolerancia"
            }.${presence.absence.reportRequired ? " Corresponde avisar a CX." : ""}`}
          >
            <span aria-hidden="true">⚠</span>
            Paciente sin conectar · {presence.absence.minutesElapsed} min
            <span className="underline">Reportar ausencia</span>
          </button>
        )}
        <span className="flex-1" />
        <button
          className={meetBtn.dangerSm}
          onClick={() => setUrgencyOpen(true)}
          disabled={sharing}
          title={sharing ? "Oculto mientras compartís pantalla" : undefined}
        >
          Urgencia
        </button>
        <button
          className={meetBtn.secondarySm}
          onClick={() => setPanelOpen((o) => !o)}
          disabled={sharing}
          aria-pressed={panelOpen}
          title={sharing ? "Oculto mientras compartís pantalla" : "Alt+P"}
        >
          <svg
            viewBox="0 0 20 20"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="mr-1.5 inline-block align-[-3px]"
          >
            <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
            <path d="M12.5 3.5v13" />
            <path
              d={panelOpen ? "M7.5 7.5 9.5 10l-2 2.5" : "M9.5 7.5 7.5 10l2 2.5"}
            />
          </svg>
          {sharing
            ? "Panel oculto (compartiendo)"
            : panelOpen
              ? "Ocultar panel"
              : "Panel privado"}
        </button>
      </div>

      <div className="flex gap-3">
        <div ref={frameBox} className="min-w-0 flex-1">
          <iframe
            ref={frame}
            src={url}
            onLoad={onFrameLoad}
            title="Videollamada"
            allow="camera; microphone; display-capture; autoplay; clipboard-write; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ height }}
            className="block w-full rounded-2xl border-0 bg-black"
          />
        </div>

        {panelOpen && !sharing && (
          <aside
            style={{ height }}
            className="w-[26rem] shrink-0 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
            aria-label="Panel privado de la sesión"
          >
            <div className="mb-3 flex gap-1" role="tablist">
              {TABS.map(([id, label]) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={tab === id}
                  onClick={() => setTab(id)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === id ? "bg-[#6D57BC] text-white" : "border border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "clinical" && (
              <ClinicalTab
                sessionId={session.sessionId}
                processId={session.processId}
                readOnly={impersonated}
              />
            )}
            {tab === "info" && (
              <InfoTab
                processId={session.processId}
                patient={patient}
                contacts={contacts}
                readOnly={impersonated}
              />
            )}
            {tab === "policies" && <PoliciesTab />}
          </aside>
        )}
      </div>

      <ExitGate
        decision={exitOpen ? decision : null}
        onStay={() => setExitOpen(false)}
        onReportAbsence={() => {
          setExitOpen(false);
          setAbsenceOpen(true);
        }}
        onLeave={leave}
      />
      <ReportAbsenceModal
        open={absenceOpen}
        onClose={() => {
          setAbsenceOpen(false);
          setAbsenceReported(true);
        }}
        sessionId={session.sessionId}
        sessionStartMs={startMs}
        patientName={patient?.fullname || "Paciente"}
        patientEmail={patient?.email}
        processId={session.processId}
      />
      <UrgencyDialog
        open={urgencyOpen && !sharing}
        onClose={() => setUrgencyOpen(false)}
        patient={patient}
        contacts={contacts}
        sessionStart={session.start}
      />
    </div>
  );
}
